import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { authService } from '@services/authService'
import { ShieldAlert, RefreshCw, LogOut, X } from 'lucide-react'
import toast from 'react-hot-toast'

// Constantes de Inatividade (20 minutos totais: 19 min ocioso + 1 min aviso)
const TOTAL_SESSION_LIMIT_MS = 20 * 60 * 1000 // 20 minutos de limite absoluto de inatividade
const WARNING_TIME_MS = 19 * 60 * 1000 // 19 minutos de ociosidade antes do aviso
const COUNTDOWN_SECONDS = 60
const STORAGE_KEY = 'gsp_last_active_timestamp'

export const SessionTimeoutModal: React.FC = () => {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const activePerspective = useAuthStore((s) => s.activePerspective)
  const logout = useAuthStore((s) => s.logout)
  const refreshTokens = useAuthStore((s) => s.refreshTokens)

  const [isWarningOpen, setIsWarningOpen] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(COUNTDOWN_SECONDS)
  const [isExtending, setIsExtending] = useState(false)

  // Refs de controle de ciclo de vida
  const isWarningOpenRef = useRef(false)
  const lastThrottleRef = useRef(0)
  const broadcastRef = useRef<BroadcastChannel | null>(null)
  const isLoggingOutRef = useRef(false)

  // O Portal do Aluno possui regras de sessão estendidas próprias
  const isStudentPortal =
    activePerspective === 'student' ||
    user?.role === 'student' ||
    user?.role === 'aluno' ||
    (typeof window !== 'undefined' && window.location.pathname.includes('/portal-aluno'))

  // Detecção de PWA instalado (Standalone no dispositivo)
  const isPWA = typeof window !== 'undefined' && (
    (typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)').matches : false) ||
    Boolean((window.navigator as Navigator & { standalone?: boolean })?.standalone)
  )

  // O PWA opera em sandbox isolado do dispositivo com janela diária de 24h
  // Navegadores web abertos no desktop mantêm a política de 20 minutos
  const sessionLimitMs = isPWA ? 24 * 60 * 60 * 1000 : TOTAL_SESSION_LIMIT_MS
  const warningThresholdMs = isPWA ? sessionLimitMs - (60 * 1000) : WARNING_TIME_MS

  const notifyOtherTabs = useCallback((type: string) => {
    try {
      broadcastRef.current?.postMessage({ type, timestamp: Date.now() })
    } catch {
      // Ignora erro de postMessage
    }
  }, [])

  // Atualiza o timestamp de última atividade humana real
  const recordUserActivity = useCallback(() => {
    if (!isAuthenticated || isStudentPortal || isLoggingOutRef.current) {
      return
    }
    const now = Date.now()
    if (now - lastThrottleRef.current < 1000) {
      return
    }
    lastThrottleRef.current = now
    try {
      localStorage.setItem(STORAGE_KEY, now.toString())
    } catch {
      // Ignora erro de quota
    }
  }, [isAuthenticated, isStudentPortal])

  // Logout rigoroso por inatividade com limpeza total, auditoria no backend e redirecionamento seguro
  const handleLogout = useCallback((shouldToast = true) => {
    if (isLoggingOutRef.current) {
      return
    }
    isLoggingOutRef.current = true
    isWarningOpenRef.current = false
    setIsWarningOpen(false)

    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignora
    }

    notifyOtherTabs('SESSION_LOGOUT')

    let cleanedUp = false
    const finish = () => {
      if (cleanedUp) {
        return
      }
      cleanedUp = true
      logout()
      if (shouldToast) {
        const msg = isPWA 
          ? 'Sessão de 24 horas encerrada para a segurança do seu dispositivo.' 
          : 'Sessão encerrada por inatividade para sua segurança.'
        toast.error(msg, {
          id: 'session-timeout-toast',
          duration: 6000
        })
      }
      navigate('/login')
    }

    // Timeout de segurança (1.2s) para garantir saída mesmo se a conexão estiver instável
    const timer = setTimeout(finish, 1200)

    // Notifica o backend para auditoria e revogação formal enquanto os tokens ainda estão preservados
    authService.logout()
      .catch(() => {
        /* Ignora falha de rede ao tentar revogar token no logout */
      })
      .finally(() => {
        clearTimeout(timer)
        finish()
      })
  }, [logout, navigate, notifyOtherTabs, isPWA])

  // Verificação rigorosa do tempo decorrido baseada em Timestamp Real
  const checkSessionStatus = useCallback(() => {
    if (!isAuthenticated || isStudentPortal || isLoggingOutRef.current) {
      return
    }

    let lastActive = Date.now()
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        lastActive = Number.parseInt(raw, 10)
      } else {
        localStorage.setItem(STORAGE_KEY, lastActive.toString())
      }
    } catch {
      lastActive = Date.now()
    }

    const now = Date.now()
    const idleMs = now - lastActive

    // 1. Passou do limite da sessão (20 min no browser web / 24h no PWA)
    if (idleMs >= sessionLimitMs) {
      handleLogout(true)
      return
    }

    // 2. Está no intervalo de aviso (últimos 60s)
    if (idleMs >= warningThresholdMs) {
      const remainingMs = sessionLimitMs - idleMs
      const remainingSec = Math.max(1, Math.ceil(remainingMs / 1000))
      setSecondsRemaining(remainingSec)
      if (!isWarningOpenRef.current) {
        isWarningOpenRef.current = true
        setIsWarningOpen(true)
      }
      return
    }

    // 3. Menos de 19 minutos de ociosidade (ou menos de 23h59 no PWA): fecha o aviso caso esteja aberto
    if (isWarningOpenRef.current) {
      isWarningOpenRef.current = false
      setIsWarningOpen(false)
      setSecondsRemaining(COUNTDOWN_SECONDS)
    }
  }, [isAuthenticated, isStudentPortal, handleLogout, sessionLimitMs, warningThresholdMs])

  // Sincronização BroadcastChannel e Storage inter-abas
  useEffect(() => {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      const channel = new BroadcastChannel('gsp_session_sync')
      broadcastRef.current = channel

      channel.onmessage = (event) => {
        if (event.data?.type === 'SESSION_RESET' || event.data?.type === 'SESSION_EXTENDED') {
          isWarningOpenRef.current = false
          setIsWarningOpen(false)
          setSecondsRemaining(COUNTDOWN_SECONDS)
          checkSessionStatus()
        } else if (event.data?.type === 'SESSION_LOGOUT') {
          handleLogout(false)
        }
      }

      return () => {
        channel.close()
      }
    }
  }, [handleLogout, checkSessionStatus])

  // Ação quando o usuário continua navegando ou cancela o aviso
  const handleNavigationActivity = useCallback(async () => {
    const now = Date.now()
    try {
      localStorage.setItem(STORAGE_KEY, now.toString())
    } catch {
      // Ignora
    }

    isWarningOpenRef.current = false
    setIsWarningOpen(false)
    setSecondsRemaining(COUNTDOWN_SECONDS)
    notifyOtherTabs('SESSION_RESET')

    // Renova os tokens no backend de forma transparente
    try {
      await refreshTokens()
    } catch (err) {
      console.warn('Renovação de sessão por navegação:', err)
    }
  }, [refreshTokens, notifyOtherTabs])

  // Clique explícito no botão "Manter Conectado (+5 min)"
  const handleExtendSessionExplicit = async () => {
    setIsExtending(true)
    try {
      await refreshTokens()
      await authService.me().catch(() => {
        /* Ignora falha silenciosa de checagem do perfil */
      })

      const now = Date.now()
      try {
        localStorage.setItem(STORAGE_KEY, now.toString())
      } catch {
        // Ignora
      }

      isWarningOpenRef.current = false
      setIsWarningOpen(false)
      setSecondsRemaining(COUNTDOWN_SECONDS)
      notifyOtherTabs('SESSION_EXTENDED')

      toast.success('Sessão renovada por mais 5 minutos!', {
        id: 'session-extended-toast'
      })
    } catch (err) {
      console.error('Falha ao renovar sessão explicitamente:', err)
      toast.error('Não foi possível renovar a sessão. Por favor, faça login novamente.')
      handleLogout(false)
    } finally {
      setIsExtending(false)
    }
  }

  // Monitoramento contínuo, resiliente a abas minimizadas/segundo plano (Page Visibility API)
  useEffect(() => {
    if (!isAuthenticated || isStudentPortal) {
      isWarningOpenRef.current = false
      setIsWarningOpen(false)
      isLoggingOutRef.current = false
      return
    }

    isLoggingOutRef.current = false

    // Inicializa timestamp se ausente
    if (!localStorage.getItem(STORAGE_KEY)) {
      try {
        localStorage.setItem(STORAGE_KEY, Date.now().toString())
      } catch {
        // Ignora
      }
    }

    // Tick contínuo a cada 1 segundo
    const interval = setInterval(() => {
      checkSessionStatus()
    }, 1000)

    // Listener de eventos de atividade humana
    const onUserActivity = () => {
      // Se o aviso estiver aberto e o usuário interagir, fecha e renova
      if (isWarningOpenRef.current) {
        void handleNavigationActivity()
        return
      }
      recordUserActivity()
    }

    // Ouvintes de visibilidade de aba para combater o congelamento do navegador (Tab Throttling)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionStatus()
      }
    }

    const onWindowFocus = () => {
      checkSessionStatus()
    }

    const onStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        checkSessionStatus()
      }
    }

    const events = ['mousedown', 'keydown', 'touchstart', 'click', 'scroll']
    events.forEach((evt) => {
      window.addEventListener(evt, onUserActivity, { passive: true })
    })

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onWindowFocus)
    window.addEventListener('storage', onStorageChange)

    return () => {
      clearInterval(interval)
      events.forEach((evt) => {
        window.removeEventListener(evt, onUserActivity)
      })
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onWindowFocus)
      window.removeEventListener('storage', onStorageChange)
    }
  }, [isAuthenticated, isStudentPortal, checkSessionStatus, recordUserActivity, handleNavigationActivity])

  // Se não estiver autenticado ou o aviso não estiver visível, não renderiza a UI
  if (!isAuthenticated || isStudentPortal || !isWarningOpen) {
    return null
  }

  const progressPercent = Math.max(0, Math.min(100, (secondsRemaining / COUNTDOWN_SECONDS) * 100))
  let renewButtonLabel = 'Cancelar & Renovar (+20 min)'
  if (isExtending) {
    renewButtonLabel = 'Renovando...'
  } else if (isPWA) {
    renewButtonLabel = 'Manter Conectado'
  }

  return (
    <aside
      aria-label="Aviso de Expiração de Sessão"
      role="alert"
      id="session-timeout-notification"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 999999,
        width: 'calc(100vw - 48px)',
        maxWidth: 440,
        backgroundColor: 'rgba(18, 20, 28, 0.96)',
        border: '1px solid rgba(179, 143, 79, 0.45)',
        borderRadius: 16,
        padding: '18px 20px',
        boxShadow: '0 20px 45px rgba(0, 0, 0, 0.9), 0 0 25px rgba(179, 143, 79, 0.25)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        color: '#ffffff',
        fontFamily: 'inherit',
        animation: 'slideUpNotif 0.35s cubic-bezier(0.16, 1, 0.3, 1)'
      }}
    >
      <style>{`
        @keyframes slideUpNotif {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      {/* Topo da Notificação */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              backgroundColor: 'rgba(179, 143, 79, 0.18)',
              border: '1px solid rgba(179, 143, 79, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffdf78',
              flexShrink: 0
            }}
          >
            <ShieldAlert size={22} />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#ffffff', letterSpacing: 0.3 }}>
              Sessão Expirando em {secondsRemaining}s
            </h4>
            <p style={{ margin: '2px 0 0 0', fontSize: 11, color: '#ffdf78', fontWeight: 600 }}>
              {isPWA ? 'Limite de sessão de 24h do app atingido' : 'Inatividade de 19 minutos detectada'}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleNavigationActivity}
          title="Continuar navegando e renovar"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#8892b0',
            cursor: 'pointer',
            padding: 4,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Descrição */}
      <p style={{ margin: '0 0 14px 0', fontSize: 12, color: '#c5c6c7', lineHeight: 1.5 }}>
        {isPWA
          ? 'Para segurança do seu dispositivo em sandbox, a janela de 24 horas está expirando. Toque no botão abaixo para estender sua sessão.'
          : 'Para sua segurança, a sessão encerra em 20 minutos. Ao continuar navegando ou clicar no botão abaixo, sua sessão é renovada imediatamente por mais 20 minutos.'}
      </p>

      {/* Barra de Progresso Regressiva */}
      <div
        style={{
          width: '100%',
          height: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 2,
          overflow: 'hidden',
          marginBottom: 14
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progressPercent}%`,
            backgroundColor: secondsRemaining <= 15 ? '#ef4444' : '#b38f4f',
            transition: 'width 1s linear, background-color 0.3s ease'
          }}
        />
      </div>

      {/* Botões de Ação */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handleExtendSessionExplicit}
          disabled={isExtending}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '9px 14px',
            borderRadius: 8,
            backgroundColor: '#b38f4f',
            color: '#0b0c10',
            border: 'none',
            fontSize: 12,
            fontWeight: 700,
            cursor: isExtending ? 'not-allowed' : 'pointer',
            opacity: isExtending ? 0.6 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(179, 143, 79, 0.3)'
          }}
        >
          <RefreshCw size={14} className={isExtending ? 'animate-spin' : ''} />
          <span>{renewButtonLabel}</span>
        </button>

        <button
          type="button"
          onClick={() => handleLogout(true)}
          disabled={isExtending}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '9px 14px',
            borderRadius: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#8892b0',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <LogOut size={14} />
          <span>Encerrar Agora</span>
        </button>
      </div>
    </aside>
  )
}
